import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InfoGrid, ReportFooterTimestamp } from '../../../utils/reportUtils';
import { HysteroscopyType } from './hysteroscopy.helper';

interface HysteroscopyReportProps {
  data: HysteroscopyType;
}

const HysteroscopyReport: React.FC<HysteroscopyReportProps> = ({ data }) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');

  const hasValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== '';
  };

  const buildGridData = (fields: Array<[string, string | number | null | undefined]>): string[][] => {
    const filtered = fields
      .filter(([, value]) => hasValue(value))
      .map(([label, value]) => [label, String(value)] as [string, string]);

    const rows: string[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      const left = filtered[i];
      const right = filtered[i + 1];
      rows.push([
        left?.[0] ?? '',
        left?.[1] ?? '',
        right?.[0] ?? '',
        right?.[1] ?? '',
      ]);
    }
    return rows;
  };

  const allFields = buildGridData([
    [tGeneral('date', { defaultValue: 'Date' }), data.date],
    [tSurgery('surgeonName', { defaultValue: 'Surgeon Name' }), data.surgeon_name],
    [tSurgery('assistantSurgeon', { defaultValue: 'Assistant Surgeon' }), data.assistant_surgeon_name],
    [tSurgery('anesthetist', { defaultValue: 'Anesthetist' }), data.anesthetist_name],
    [tSurgery('procedureDone', { defaultValue: 'Procedure Done' }), data.procedure_done_label],
    [tSurgery('procedureDurationMin', { defaultValue: 'Procedure Duration (min)' }), data.procedure_duration],
    [tSurgery('cervicalOs', { defaultValue: 'Cervical Os' }), data.cervical_os_label],
    [tSurgery('endocervicalConal', { defaultValue: 'Endocervical Canal' }), data.endocervical_conal],
    [tSurgery('uterusCervicalLengthMm', { defaultValue: 'Uterus Cervical Length (mm)' }), data.uterus_cervical_length],
    [tSurgery('cavity', { defaultValue: 'Cavity' }), data.cavity_label],
    [tSurgery('endometrium', { defaultValue: 'Endometrium' }), data.endometrium_label],
    [tSurgery('rightOstia', { defaultValue: 'Right Ostia' }), data.right_ostia],
    [tSurgery('leftOstia', { defaultValue: 'Left Ostia' }), data.left_ostia],
    [tSurgery('ebHpe', { defaultValue: 'EB-HPE' }), data.eb_hpe],
    [tSurgery('otherDetails', { defaultValue: 'Other Details' }), data.other_details],
    [tGeneral('remark', { defaultValue: 'Remark' }), data.remarks],
    [tSurgery('doneBy', { defaultValue: 'Done By' }), data.done_by_name],
  ]);

  return (
    <Box sx={{ p: '8px 35px 35px 35px', backgroundColor: '#fff', color: '#333', fontFamily: 'Arial, sans-serif' }}>
      {allFields.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <InfoGrid data={allFields} />
        </Box>
      )}

      <ReportFooterTimestamp />
    </Box>
  );
};

export default HysteroscopyReport;
